"use client";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import axios from "../axios-base";

export const useWishStore = create(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,
      isOpen: false,

      setIsOpen: (isOpen) => set({ isOpen }),
      toggleWishDrawer: () => set((state) => ({ isOpen: !state.isOpen })),

      isWishlisted: (bookId) => {
        if (!bookId) return false;
        return get().items.some(
          (i) => i?.book?._id === bookId || i?._id === bookId,
        );
      },

      addToWishlist: async (book, user = null) => {
        if (!book?._id) return;
        const { items } = get();
        const exists = items.some(
          (i) => i?.book?._id === book._id || i?._id === book._id,
        );
        if (exists) return;

        const localItem = user ? { book } : book;
        set({ items: [...items, localItem] });

        if (user) {
          set({ isLoading: true });
          try {
            const res = await axios.post("gerelt/wishlist", {
              bookId: book._id,
            });
            if (res.data.success) {
              set({ items: res.data.data.items });
            }
          } catch (err) {
            console.error("Failed to add wishlist", err);
          } finally {
            set({ isLoading: false });
          }
        }
      },

      removeFromWishlist: async (bookId, user = null) => {
        if (!bookId) return;
        const { items } = get();
        const newItems = items.filter(
          (i) => (i?.book?._id || i?._id) !== bookId,
        );
        set({ items: newItems });

        if (user) {
          set({ isLoading: true });
          try {
            const res = await axios.delete(`gerelt/wishlist/${bookId}`);
            if (res.data.success) {
              set({ items: res.data.data.items });
            }
          } catch (err) {
            console.error("Failed to remove wishlist", err);
          } finally {
            set({ isLoading: false });
          }
        }
      },

      toggleWishlist: async (book, user = null) => {
        const inWish = get().isWishlisted(book?._id);
        if (inWish) return get().removeFromWishlist(book?._id, user);
        return get().addToWishlist(book, user);
      },

      syncLocalWithServer: async (user) => {
        if (!user) return;
        const { items } = get();

        if (items.length === 0) {
          get().fetchWishlist(user);
          return;
        }

        set({ isLoading: true });
        try {
          const payload = items
            .map((i) => ({ book: i?._id || i?.book?._id }))
            .filter((i) => i.book);
          const res = await axios.post("gerelt/wishlist/sync", {
            items: payload,
          });
          if (res.data.success) {
            set({ items: res.data.data.items });
          }
        } catch (err) {
          console.error("Sync wishlist failed", err);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchWishlist: async (user) => {
        if (!user) return;
        set({ isLoading: true });
        try {
          const res = await axios.get("gerelt/wishlist");
          if (res.data.success) {
            set({ items: res.data.data.items });
          }
        } catch (err) {
          console.error("Fetch wishlist failed", err);
        } finally {
          set({ isLoading: false });
        }
      },

      clearWishlist: () => set({ items: [] }),
    }),
    {
      name: "wishlist-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    },
  ),
);
